"use client"

import type React from "react"
import { useState } from "react"

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";

import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Email from "@mui/icons-material/Email";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";

import { isValidEmail, isDuplicateEmail } from "../utils/validation-utils"
import { useSelector } from "react-redux";

interface ContentsManagerProps {
  contentsManager: string[]
  onUpdate: (administrators: string[]) => void
}

export const ContentsManager: React.FC<ContentsManagerProps> = ({ contentsManager, onUpdate }) => {
  const userInfo = useSelector((state: any) => state.user);
  
  const [newEmail, setNewEmail] = useState("")
  const [error, setError] = useState("")

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase()

    if (!email) {
      setError("이메일을 입력해주세요.")
      return
    }

    if (!isValidEmail(email)) {
      setError("올바른 이메일 형식이 아닙니다.")
      return
    }

    if (isDuplicateEmail(email, contentsManager)) {
      setError("이미 등록된 이메일입니다.")
      return
    }

    onUpdate([...contentsManager, email])
    setNewEmail("")
    setError("")
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    onUpdate(contentsManager.filter((email) => email !== emailToRemove))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddEmail()
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <AdminPanelSettings sx={{ mr: 1, color: "text.secondary" }} />
        <Typography variant="subtitle2">컨텐츠 관리자:</Typography>
      </Box>

      {/* 이메일 추가 입력 */}
      {userInfo.role === "admin" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            placeholder="컨텐츠 관리자 이메일 입력"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value)
              if (error) setError("")
            }}
            onKeyPress={handleKeyPress}
            error={!!error}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddEmail} disabled={!newEmail.trim()}>
            추가
          </Button>
        </Box>
      )}

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 관리자 목록 */}
      <Paper variant="outlined" sx={{ maxHeight: 200, overflow: "auto" }}>
        {contentsManager.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            <Email sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">등록된 컨텐츠 관리자가 없습니다</Typography>
            <Typography variant="caption">컨텐츠 관리자 이메일을 추가해주세요</Typography>
          </Box>
        ) : (
          <List dense>
            {contentsManager.map((email, index) => (
              <Box key={email}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Email sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography variant="body2">{email}</Typography>
                        <Chip label="컨텐츠 관리자" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                      </Box>
                    }
                  />
                  {userInfo.role === "admin" && (
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" onClick={() => handleRemoveEmail(email)} color="error">
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
                {index < contentsManager.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* 관리자 수 표시 */}
      {contentsManager.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">
            총 {contentsManager.length}명의 컨텐츠 관리자가 등록되어 있습니다
          </Typography>
        </Box>
      )}
    </Box>
  )
}


